import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { apiConfig } from '@tagna/config';

export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = apiConfig.baseUrl;

    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: apiConfig.timeout,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          this.clearToken();
          // Use OIDC SSO redirect path instead of direct login
          window.location.href = '/auth/sso-redirect';
        }
        return Promise.reject(error);
      }
    );
  }

  private getToken(): string | null {
    // Check sessionStorage first (OIDC stores tokens here), then localStorage for backward compat
    return sessionStorage.getItem('tagna_auth_token') || localStorage.getItem('tagna_auth_token');
  }

  public setToken(token: string): void {
    sessionStorage.setItem('tagna_auth_token', token);
    // Keep localStorage in sync for backward compatibility
    localStorage.setItem('tagna_auth_token', token);
  }

  public clearToken(): void {
    sessionStorage.removeItem('tagna_auth_token');
    localStorage.removeItem('tagna_auth_token');
    localStorage.removeItem('tagna_user_data');
  }

  public async get<T>(url: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.get<T>(url, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return this.handleError<T>(error);
    }
  }

  public async post<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post<T>(url, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return this.handleError<T>(error);
    }
  }

  public async put<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.put<T>(url, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return this.handleError<T>(error);
    }
  }

  public async delete<T>(url: string): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.delete<T>(url);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return this.handleError<T>(error);
    }
  }

  private handleError<T>(error: unknown): ApiResponse<T> {
    const axiosError = error as AxiosError;
    
    if (axiosError.response?.data) {
      const errorData = axiosError.response.data as Record<string, unknown>;
      return {
        success: false,
        data: null as T,
        message: (typeof errorData.message === 'string' ? errorData.message : 
                 (errorData.error && typeof errorData.error === 'object' && errorData.error !== null && 
                  'message' in errorData.error && typeof errorData.error.message === 'string' ? 
                  errorData.error.message : 'An error occurred')),
        errors: Array.isArray(errorData.errors) ? errorData.errors as string[] : [],
      };
    }

    if (axiosError.message) {
      return {
        success: false,
        data: null as T,
        message: axiosError.message,
      };
    }

    return {
      success: false,
      data: undefined as unknown as T,
      message: 'Network error occurred',
    };
  }
}

export const apiClient = new ApiClient();